"use client";

import { useLenis } from "lenis/react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from "react";

import type { ArticleTocItem } from "@/lib/article-toc";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";
import { useCcScrollEffect } from "@/lib/use-cc-scroll-effect";

type CcArticleTocProps = {
  items: ArticleTocItem[];
  label: string;
};

const NAV_OFFSET_PX = 96;

export function CcArticleToc({ items, label }: CcArticleTocProps) {
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);
  const navRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const headingsRef = useRef<HTMLElement[]>([]);
  const frameRef = useRef<number | null>(null);
  const navigationRef = useRef<{ id: string; timer?: number } | null>(null);
  const lenis = useLenis();
  const reducedMotion = useCcPrefersReducedMotion();

  const updateFromScroll = useCallback(() => {
    frameRef.current = null;
    if (navigationRef.current) return;
    const headings = headingsRef.current;
    if (!headings.length) return;

    // Inspect every heading, including ones skipped by a fast scroll. The current
    // section stays selected throughout the space before the next heading.
    const readingLine = (parseFloat(getComputedStyle(headings[0]).scrollMarginTop) || NAV_OFFSET_PX) + 8;
    let next = headings[0].id;
    for (const heading of headings) {
      if (heading.getBoundingClientRect().top > readingLine) break;
      next = heading.id;
    }
    setActiveId((current) => current === next ? current : next);
  }, []);

  const scheduleUpdate = useCallback(() => {
    if (frameRef.current === null) frameRef.current = requestAnimationFrame(updateFromScroll);
  }, [updateFromScroll]);

  useCcScrollEffect(scheduleUpdate);

  const finishNavigation = useCallback(() => {
    if (navigationRef.current?.timer) window.clearTimeout(navigationRef.current.timer);
    navigationRef.current = null;
    scheduleUpdate();
  }, [scheduleUpdate]);

  useEffect(() => {
    headingsRef.current = items
      .map((item) => document.getElementById(item.id))
      .filter((node): node is HTMLElement => Boolean(node));
    const content = navRef.current?.closest(".cc-article-toc-layout")?.querySelector(".cc-article-toc-layout__content");
    const observer = new ResizeObserver(scheduleUpdate);
    if (content) observer.observe(content);
    scheduleUpdate();
    return () => {
      observer.disconnect();
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      if (navigationRef.current?.timer) window.clearTimeout(navigationRef.current.timer);
      navigationRef.current = null;
    };
  }, [items, scheduleUpdate]);

  useEffect(() => {
    const interrupt = () => {
      if (!navigationRef.current) return;
      // Stop a link jump before handing control back to wheel, touch or keyboard.
      if (lenis) lenis.scrollTo(lenis.scroll, { immediate: true });
      else window.scrollTo({ top: window.scrollY, behavior: "instant" });
      finishNavigation();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " "].includes(event.key)) interrupt();
    };
    const onScrollEnd = () => {
      if (!lenis && navigationRef.current) finishNavigation();
    };
    window.addEventListener("wheel", interrupt, { passive: true });
    window.addEventListener("touchstart", interrupt, { passive: true });
    window.addEventListener("pointerdown", interrupt, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("scrollend", onScrollEnd);
    return () => {
      window.removeEventListener("wheel", interrupt);
      window.removeEventListener("touchstart", interrupt);
      window.removeEventListener("pointerdown", interrupt);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scrollend", onScrollEnd);
    };
  }, [lenis, finishNavigation]);

  useLayoutEffect(() => {
    const nav = navRef.current;
    const list = listRef.current;
    if (!nav || !list) return;
    const updateIndicator = () => {
      const link = list.querySelector<HTMLElement>('[aria-current="location"]');
      if (!link || !nav.clientHeight) return;
      const linkRect = link.getBoundingClientRect();
      const listRect = list.getBoundingClientRect();
      list.style.setProperty("--cc-toc-indicator-y", `${linkRect.top - listRect.top + 4}px`);
      list.style.setProperty("--cc-toc-indicator-height", `${Math.max(0, linkRect.height - 8)}px`);
      list.style.setProperty("--cc-toc-indicator-opacity", "1");
      const navRect = nav.getBoundingClientRect();
      // Only move the sidebar's own scroll position; never move the article here.
      const delta = linkRect.top < navRect.top + 8
        ? linkRect.top - navRect.top - 8
        : linkRect.bottom > navRect.bottom - 8 ? linkRect.bottom - navRect.bottom + 8 : 0;
      if (delta) nav.scrollTo({ top: nav.scrollTop + delta, behavior: reducedMotion ? "instant" : "smooth" });
    };
    updateIndicator();
    const observer = new ResizeObserver(updateIndicator);
    observer.observe(list);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [activeId, items, reducedMotion]);

  const onLinkClick = useCallback((event: MouseEvent<HTMLAnchorElement>, id: string) => {
    // Lenis also listens for anchors on window. Handle this link exactly once.
    event.stopPropagation();
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    finishNavigation();
    const navigation: { id: string; timer?: number } = { id };
    navigationRef.current = navigation;
    setActiveId(id);
    const complete = () => {
      if (navigationRef.current === navigation) finishNavigation();
    };
    history.replaceState(history.state, "", `#${encodeURIComponent(id)}`);
    if (lenis) {
      // Lenis honors the target's scroll-margin-top, matching native anchors.
      lenis.scrollTo(target, {
        duration: 0.85,
        easing: (t) => 1 - Math.pow(1 - t, 3),
        immediate: reducedMotion,
        onComplete: complete,
      });
    } else {
      const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || NAV_OFFSET_PX;
      window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - margin, behavior: reducedMotion ? "instant" : "smooth" });
      if (reducedMotion) complete();
      else navigation.timer = window.setTimeout(complete, 1800);
    }
  }, [finishNavigation, lenis, reducedMotion]);

  return (
    <nav ref={navRef} className="cc-article-toc" aria-label={label} data-lenis-prevent>
      <p className="cc-article-toc__label">{label}</p>
      <ul ref={listRef} className="cc-article-toc__list">
        {items.map((item) => {
          const isActive = item.id === activeId;
          return (
            <li key={item.id} className={`cc-article-toc__item cc-article-toc__item--h${item.level}${isActive ? " is-active" : ""}`}>
              <a href={`#${item.id}`} className="cc-article-toc__link" aria-current={isActive ? "location" : undefined} onClick={(event) => onLinkClick(event, item.id)}>
                {item.title}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
